/**
 * useSavedViews — surface-agnostic hook that owns all saved-view orchestration.
 *
 * The caller provides a SavedViewSurface adapter describing how to read and
 * write filter/view state on the hosting surface (list, board, pipeline, etc.).
 * The hook handles: view fetching, apply, dirty tracking against the stored
 * view, the explicit save/discard flow, URL persistence, warning presentation,
 * and the save-modal lifecycle.
 *
 * Saved views are NEVER auto-saved: every update to an existing view is an
 * explicit user action (saveViewChanges), and local edits are discardable
 * (discardViewChanges). Unsaved FILTER divergence is mirrored into the
 * `?filters=` URL param by useListUrlSync so dirty states survive reload and
 * are shareable.
 */

import { useQueryClient } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import type { ActiveListView } from '@/lib/crm/core/types';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import {
  fetchSavedFilterViewForApply,
  useCreateSavedFilterView,
  useDeleteSavedFilterView,
  useSavedFilterViewsList,
  useUpdateSavedFilterView,
} from '@/lib/crm/list/hooks/useSavedFilterViews';
import { logger } from '@/lib/logger';
import type { CrmFilter, FilterGroup } from '@/types/crm';
import type {
  SavedFilterDefinition,
  SavedFilterView,
  SavedFilterViewState,
  SavedFilterViewWarning,
} from '@/types/savedFilterView';
import {
  canonicalizeFilterGroups,
  convertConditionGroupsToFilterGroups,
  filterGroupsToConditionGroups,
  partitionApplicableFilterGroups,
} from '@/utils/filterUtils';

/**
 * Permission group whose members may edit any saved view (mirrors the
 * backend's `_is_owner_or_admin` write gate — NOT `ahoy_schema_admin`).
 */
const VIEW_ADMIN_PERMISSION_GROUP = 'ahoy_permissions_admin';

/**
 * Normalized equality for two view-state snapshots, used by dirty tracking.
 *
 * Field-by-field (never raw JSON.stringify of the whole object): the stored
 * snapshot comes from a server response whose key order is not guaranteed,
 * and `columnVisibility` is a sparse map where an explicit `true` entry and
 * an absent entry both mean "visible" — neither difference is a real edit.
 */
function savedViewStatesEqual(
  a: SavedFilterViewState,
  b: SavedFilterViewState
): boolean {
  const hiddenColumns = (state: SavedFilterViewState): string =>
    JSON.stringify(
      Object.entries(state.columnVisibility ?? {})
        .filter(([, visible]) => visible === false)
        .map(([column]) => column)
        .sort()
    );
  const sortsEqual =
    (a.sortDescriptor === null) === (b.sortDescriptor === null) &&
    (a.sortDescriptor === null ||
      (a.sortDescriptor.column === b.sortDescriptor?.column &&
        a.sortDescriptor.direction === b.sortDescriptor?.direction));
  return (
    a.version === b.version &&
    JSON.stringify(a.columnOrder) === JSON.stringify(b.columnOrder) &&
    hiddenColumns(a) === hiddenColumns(b) &&
    sortsEqual
  );
}

export const EMPTY_VIEW_STATE: SavedFilterViewState = {
  version: 1,
  columnOrder: [],
  columnVisibility: {},
  sortDescriptor: null,
};

// Caps URL-hydration retries so a repeated-abort scenario falls back to "All"
// instead of retrying forever. One retry is enough to recover from a
// StrictMode-style synthetic double-invoke abort; further aborts are treated
// as a genuine, unrecoverable condition worth surfacing in logs.
const MAX_HYDRATION_RETRIES = 1;

export type ApplyViewOutcome = 'applied' | 'aborted' | 'failed';

/**
 * Adapter interface that a hosting surface (list, board, pipeline…) must
 * provide so useSavedViews can drive it without knowing its internals.
 *
 * All function properties MUST be wrapped with useCallback on the consumer
 * side so that useSavedViews' memoisation is effective.
 */
export interface SavedViewSurface<TViewState = unknown> {
  /** Backend scope key used for listing/creating views (e.g. "ahoy_contact"). */
  targetEntityType: string;
  /** Returns the surface's current filter condition groups. Called during render; must be stable via useCallback. */
  getFilterConditionGroups(): FilterConditionGroup[];
  /**
   * Applies the passed condition groups to the surface.
   * MUST use the argument directly — no stale-closure re-reads.
   */
  applyFilterConditionGroups(groups: FilterConditionGroup[]): void;
  /**
   * Snapshot the surface-specific view state (columns, sort, etc.).
   * Omitting this function disables auto-save for this surface and causes
   * EMPTY_VIEW_STATE to be stored when opening the save modal.
   */
  captureViewState?(): TViewState;
  /** Restore a previously captured view state snapshot. */
  applyViewState?(state: TViewState): void;
  /**
   * Return false for clauses that cannot be applied on this surface; they are
   * silently dropped and a client-side warning is added to the toast.
   * Defaults to all-applicable when omitted.
   */
  isClauseApplicable?(clause: CrmFilter): boolean;
}

/**
 * External owner of the single "active view" identity (from useActiveListView).
 * useSavedViews defers to this controller instead of owning `activeViewId` and
 * the URL param itself: it derives the active saved-view id from `activeView`,
 * commits an applied saved view via `setActiveView` (atomically inside the
 * apply transition), and restores the default view (built-in or "All") via
 * `resetToDefaultView` when a saved-view apply fails during URL hydration or the
 * active saved view is deleted.
 */
export interface SavedViewsActiveViewController {
  activeView: ActiveListView | null;
  setActiveView: Dispatch<SetStateAction<ActiveListView | null>>;
  resetToDefaultView: () => void;
}

export interface ApplyViewOptions {
  /**
   * Skip applying the view's stored filters to the surface, applying only its
   * view_state (columns/sort) and identity. Used when the surface's current
   * filters are deliberate URL overrides (`?filters=`) that must survive the
   * apply — e.g. a deep link `?savedView=X&filters=Y` or a back/forward
   * restore of a dirty URL.
   */
  preserveSurfaceFilters?: boolean;
}

export interface UseSavedViewsOptions {
  /**
   * Reads the CURRENT `?filters=` override groups from the URL (null when the
   * param is absent/invalid). Must be a stable callback — the page implements
   * it via a searchParams ref + decodeFiltersParam. When provided, hydration
   * and back/forward re-applies preserve any URL filter overrides instead of
   * clobbering them with the view's stored filters.
   */
  getUrlFilterOverrides?: () => FilterGroup[] | null;
}

/**
 * Tri-state for "does the surface's current state differ from the active saved
 * view's stored state?" — 'unknown' while the views list or a pending apply
 * hasn't resolved yet (callers must not treat 'unknown' as either clean or
 * dirty; the URL sync hook leaves its params untouched in that state).
 */
export type SavedViewDirtyState = 'clean' | 'dirty' | 'unknown';

export interface UseSavedViewsReturn {
  /** Ordered list of saved views for this surface, or undefined while loading. */
  views: SavedFilterView[] | undefined;
  /** Currently active saved view, or null for the ephemeral "All" view. */
  activeViewId: string | null;
  /**
   * The active saved view's server record from the list query, or null when
   * no saved view is active / the list hasn't loaded. Baseline for dirty
   * tracking; also carries `view_state.sortDescriptor` (the URL sync hook's
   * sort baseline on a saved view) and `is_owner`.
   */
  activeSavedView: SavedFilterView | null;
  /** Monotonic counter bumped each time a view is applied/selected. */
  applyToken: number;
  /** Apply a saved view by id (fetches latest from server then applies). */
  applyView(viewId: string, opts?: ApplyViewOptions): Promise<ApplyViewOutcome>;
  /**
   * Whether the surface's current FILTERS differ from the active saved view's
   * stored (surface-applicable) filter definition. Always 'clean' when no
   * saved view is active. Drives the `?filters=` URL param specifically.
   */
  filtersDirtyState: SavedViewDirtyState;
  /**
   * Whether ANY of the surface's current state (filters OR columns/sort)
   * differs from the active saved view's stored state. Saved views are never
   * auto-saved — this drives the unsaved-changes indicator and the
   * Save/Discard affordance.
   */
  viewDirtyState: SavedViewDirtyState;
  /**
   * The active saved view's stored filter groups, partitioned to the clauses
   * applicable on this surface. Null while unknown (no active saved view, the
   * views list still loading, or a throwing applicability predicate). Used to
   * restore the clean definition on discard and on back/forward to a clean
   * view URL.
   */
  activeSavedViewFilterGroups: FilterGroup[] | null;
  /**
   * True when the current user may write the active saved view (owner or a
   * member of the org's permissions-admin group — mirrors the backend gate).
   */
  canWriteActiveView: boolean;
  /**
   * Persist the surface's current filters AND view state (columns/sort, when
   * the surface captures it) to the active saved view. The ONLY way an
   * existing view is updated with surface changes — nothing auto-saves.
   */
  saveViewChanges(): void;
  /**
   * True while an explicit saveViewChanges() PATCH is in flight. Surfaces MUST
   * reflect this on the save control: the request is a network round trip, and
   * an unchanged button reads as a dropped click.
   */
  isSavingViewChanges: boolean;
  /**
   * Discard local unsaved changes: re-apply the active view's stored filter
   * definition and stored view state (columns/sort) to the surface.
   */
  discardViewChanges(): void;
  /**
   * True from the moment a saved-view pill is clicked until its filters are
   * committed to the surface (or the apply fails/aborts). Surfaces should
   * render their loading state while this is true — the apply starts with a
   * network fetch of the view, and without this flag the old data sits
   * on-screen with no feedback until that fetch resolves.
   */
  isApplyingView: boolean;
  /**
   * True while this hook's transition — the one carrying EVERY change it makes
   * to the surface (apply, discard, reset) — has not yet committed.
   *
   * During that window the surface's filters and the active-view identity may
   * describe different points in time: the identity can be updated at a
   * different priority than the filters, so React commits a frame where one has
   * moved and the other has not. That frame is a real render, and anything
   * MIRRORING this state (useListUrlSync writing `?filters=`) will publish it
   * as though it were settled, then read it back as an instruction.
   *
   * Consumers that mirror surface state outward MUST treat this as "unresolved"
   * and leave their output untouched until it clears.
   */
  surfaceTransitionPending: boolean;
  /** Select the "All"/default view: resets surface filters AND active view. */
  selectAll(): void;
  /**
   * Reset ONLY the surface (filter conditions + view state) to the base "All"
   * state, leaving the active view identity to the caller. Used when switching
   * INTO a built-in view from a saved view: the caller pairs this with the
   * controller's setActiveView({ kind: 'builtin', … }).
   */
  resetSurface(): void;
  /**
   * Invalidate any in-flight saved-view apply without resetting the surface
   * (filters/columns are left intact). Call this when selecting a built-in view
   * while a saved-view apply may still be pending, so the late apply cannot
   * commit a stale saved-view identity over the built-in selection.
   */
  cancelPendingApply(): void;
  /** Whether the save-current-view modal is open. */
  saveModalOpen: boolean;
  /** Snapshot captured when the save modal was opened; null before first open. */
  savePayload: {
    filter_definition: SavedFilterDefinition;
    view_state: SavedFilterViewState;
  } | null;
  /** Open the save-current-view modal (captures a snapshot of the current state). */
  openSaveModal(): void;
  /** Close the save-current-view modal without saving. */
  closeSaveModal(): void;
  /** Call after a view has been successfully created (shows a success toast). */
  /**
   * Call after a new view is created from the current surface. Toasts and makes
   * the new view active, so the pill and URL name what the user just saved.
   */
  onViewSaved(view?: SavedFilterView): void;
  /** Call after a view has been deleted; falls back to "All" if it was active. */
  onViewDeleted(viewId: string): void;
  /** Number of filter clauses dropped during the last applyView call. */
  lastApplyDroppedCount: number;
  /** Number of filter groups fully dropped (all clauses inapplicable) during the last applyView call. */
  lastApplyDroppedGroupCount: number;
}

export function useSavedViews<TViewState = unknown>(
  surface: SavedViewSurface<TViewState>,
  activeViewController: SavedViewsActiveViewController,
  options?: UseSavedViewsOptions
): UseSavedViewsReturn {
  const { currentOrg } = useOrg();
  const { hasPermissionGroup } = usePermissions();
  const queryClient = useQueryClient();
  // isPending is exposed (as surfaceTransitionPending) rather than discarded:
  // every state change this hook makes to the surface rides this transition,
  // so while it is pending the surface's filters and the active-view identity
  // can disagree — see the surfaceTransitionPending doc on the return type.
  const [surfaceTransitionPending, startTransition] = useTransition();

  // Held in a ref so hydration / back-forward effects can read the CURRENT
  // URL overrides without depending on the callback identity.
  const getUrlFilterOverridesRef = useRef(options?.getUrlFilterOverrides);
  getUrlFilterOverridesRef.current = options?.getUrlFilterOverrides;
  const hasUrlFilterOverrides = () =>
    (getUrlFilterOverridesRef.current?.() ?? null) != null;

  const orgId = currentOrg?.id ?? 0;

  const { activeView, setActiveView, resetToDefaultView } =
    activeViewController;
  // The active saved-view id is derived from the unified active-view identity:
  // a built-in or "All" view means no saved view is active.
  const activeViewId = activeView?.kind === 'saved' ? activeView.id : null;

  // ---------------------------------------------------------------------------
  // Core state
  // ---------------------------------------------------------------------------
  const [applyToken, setApplyToken] = useState(0);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [savePayload, setSavePayload] = useState<{
    filter_definition: SavedFilterDefinition;
    view_state: SavedFilterViewState;
  } | null>(null);
  const [lastApplyDroppedCount, setLastApplyDroppedCount] = useState(0);
  const [lastApplyDroppedGroupCount, setLastApplyDroppedGroupCount] =
    useState(0);
  // Seed `true` when a saved view is the active identity at mount: it was
  // seeded synchronously from the URL (useActiveListView) which already
  // suppressed the built-in baseline, but its filters are applied
  // asynchronously by the hydration effect below. Blocking the surface from the
  // first render prevents a deep-link from briefly running and rendering an
  // unfiltered query before the saved definition lands. Cleared on the first
  // apply's completion (applied/aborted/failed), after which isApplyingView
  // tracks only user-initiated applies.
  const [isApplyingView, setIsApplyingView] = useState(
    () => activeView?.kind === 'saved'
  );

  const applyAbortRef = useRef<AbortController | null>(null);
  const applyGenerationRef = useRef(0);
  // The saved-view id most recently handed to applyView (set synchronously in
  // its prologue), or null after selectAll/resetSurface. The back/forward
  // reconciliation effect uses this to detect an activeViewId that changed
  // WITHOUT an applyView call (popstate via useActiveListView's URL→state
  // effect) and re-apply that view — without double-applying pill clicks or
  // URL hydration, whose applyView call updates this ref before the effect
  // observes the identity change.
  const lastApplyRequestedViewIdRef = useRef<string | null>(null);

  // ---------------------------------------------------------------------------
  // Remote data / mutations
  // ---------------------------------------------------------------------------
  const { data: views } = useSavedFilterViewsList(
    currentOrg?.id,
    surface.targetEntityType
  );

  // Backs the explicit "Save changes" action. `isPending` is surfaced so the
  // button can show a pending state — without it the click has no visible
  // effect until the request resolves, and the user re-clicks. This mutation
  // instance is shared by the whole surface (not one per view), so its raw
  // `isPending` cannot be exposed directly as `isSavingViewChanges` below: a
  // save started on view A would leak a loading Save button and a disabled
  // Discard button onto view B if the user switches views before A's PATCH
  // resolves. `isSavingViewChanges` is instead derived to be true only when
  // this mutation is pending AND `savingViewIdRef` (set in saveViewChanges)
  // still points at the currently active view.
  const { mutateAsync: updateView, isPending: isUpdateViewPending } =
    useUpdateSavedFilterView(orgId, surface.targetEntityType);
  // The view id targeted by the in-flight saveViewChanges PATCH, or null when
  // no save is pending. Set synchronously when saveViewChanges is invoked;
  // cleared in a .finally() so a throw from either the success or failure
  // handler can never leave it stuck and a Save control frozen loading.
  const savingViewIdRef = useRef<string | null>(null);
  const isSavingViewChanges =
    isUpdateViewPending && savingViewIdRef.current === activeViewId;

  // Called to satisfy the spec requirement that the hook consumes all three
  // mutation hooks. Create/delete are invoked by UI components (SaveCurrentViewModal,
  // SavedViewTabs); the hook exposes only the orchestration callbacks.
  useCreateSavedFilterView(orgId, surface.targetEntityType);
  useDeleteSavedFilterView(orgId, surface.targetEntityType);

  // ---------------------------------------------------------------------------
  // applyView
  // ---------------------------------------------------------------------------
  const applyView = useCallback(
    async (
      viewId: string,
      opts?: ApplyViewOptions
    ): Promise<ApplyViewOutcome> => {
      applyGenerationRef.current += 1;
      const generation = applyGenerationRef.current;
      lastApplyRequestedViewIdRef.current = viewId;
      if (!currentOrg) {
        return 'aborted';
      }
      const preserveSurfaceFilters = opts?.preserveSurfaceFilters === true;

      // Abort any in-flight apply for this surface.
      applyAbortRef.current?.abort();
      const controller = new AbortController();
      applyAbortRef.current = controller;

      // Instant feedback BEFORE the network fetch: raise isApplyingView so the
      // surface can show its loading state (a slow fetch would otherwise leave
      // stale data on-screen with no feedback). The pill highlight moves only
      // when the apply COMMITS (setActiveView, below) — committing the saved-view
      // identity earlier would suppress the built-in baseline before the saved
      // filters land, momentarily widening the query. isApplyingView is
      // generation-guarded: a newer applyView owns the flag and this call must
      // not touch it. The active view is never optimistically changed, so no
      // path below has anything to revert.
      setIsApplyingView(true);
      const isCurrentGeneration = () =>
        applyGenerationRef.current === generation;

      let view;
      try {
        view = await fetchSavedFilterViewForApply(
          queryClient,
          currentOrg.id,
          viewId,
          controller.signal
        );
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // Superseded by a newer apply (generation moved on) or aborted on
          // unmount (still current — clear the flag so a remount doesn't inherit
          // a stuck loading state).
          if (isCurrentGeneration()) {
            setIsApplyingView(false);
          }
          return 'aborted';
        }
        if (isCurrentGeneration()) {
          setIsApplyingView(false);
        }
        toast.error("Couldn't apply this saved view. Please try again.");
        logger.warn('Failed to fetch saved view for apply', {
          component: 'saved-views',
          org_id: currentOrg.id,
          saved_view_id: viewId,
        });
        return 'failed';
      }

      // Partition CrmFilter clauses group-by-group to preserve OR semantics
      // (shared implementation with the dirty-tracking memo below).
      let droppedCount = 0;
      let droppedGroupCount = 0;
      let applicableGroups: FilterGroup[] = [];
      let droppedWarnings: SavedFilterViewWarning[] = [];
      // Tracked so a throw from surface.isClauseApplicable can be logged with
      // the clause that triggered it.
      let clauseBeingChecked: CrmFilter | null = null;

      try {
        const isApplicable = surface.isClauseApplicable;
        const trackedIsApplicable = isApplicable
          ? (clause: CrmFilter) => {
              clauseBeingChecked = clause;
              return isApplicable(clause);
            }
          : undefined;
        const partition = partitionApplicableFilterGroups(
          view.filter_definition.filter_groups,
          trackedIsApplicable
        );
        ({
          droppedCount,
          droppedGroupCount,
          applicableGroups,
          droppedWarnings,
        } = partition);
      } catch (err) {
        // A misbehaving surface.isClauseApplicable must not leave applyView's
        // returned Promise rejected — every caller (URL hydration and UI
        // click handlers) expects a resolved ApplyViewOutcome, matching the
        // fetch-failure handling above. Generation-guarded like every other
        // cleanup path: revert the optimistic highlight AND lower the applying
        // flag so a thrown predicate can't strand the surface in its loading
        // state; a newer apply owns the flag and must not be touched.
        if (isCurrentGeneration()) {
          setIsApplyingView(false);
        }
        toast.error("This saved view couldn't be applied. Please try again.");
        logger.warn(
          'surface.isClauseApplicable threw while partitioning saved view filters',
          {
            component: 'saved-views',
            saved_view_id: viewId,
            clause: clauseBeingChecked,
            error_type: (err as Error)?.name ?? 'Unknown',
          }
        );
        return 'failed';
      }

      if (droppedGroupCount > 0 || droppedCount > 0) {
        logger.info('Saved view apply dropped clauses or groups', {
          component: 'saved-views',
          saved_view_id: viewId,
          droppedGroupCount,
          droppedCount,
        });
      }

      const applicableConditionGroups =
        filterGroupsToConditionGroups(applicableGroups);

      let applyFailed = false;
      startTransition(() => {
        // Superseded by a newer apply (or a selectAll/resetSurface) before this
        // fetch resolved: skip the ENTIRE commit so a stale apply cannot clobber
        // the newer view's filters or identity. The newer owner has already
        // committed its own state. (Real fetches abort and never reach here; this
        // guards the manual-promise / already-cached races.)
        if (!isCurrentGeneration()) {
          return;
        }
        try {
          // Single atomic commit: surface filters + the unified active-view
          // identity + applyToken bump, all in one transition. Committing the
          // saved-view identity here (rather than optimistically at click time)
          // means the pill highlight, the built-in baseline suppression, and the
          // saved filters land together — never a frame with the baseline gone
          // but the saved filters not yet applied. The applyToken bump signals
          // consumers that this state change was programmatic, not a user edit.
          //
          // preserveSurfaceFilters: the surface's current filters are deliberate
          // URL overrides — apply only view_state + identity and leave them be.
          if (!preserveSurfaceFilters) {
            surface.applyFilterConditionGroups(applicableConditionGroups);
          }
          if (surface.applyViewState) {
            surface.applyViewState(view.view_state as unknown as TViewState);
          }
          // Skip setActiveView when the identity was already committed by
          // useActiveListView's preference-driven default-application effect (which
          // uses the raw setter, not userSetActiveView). Calling setActiveView here
          // when already committed would mark pristineRef=false and cause the
          // state→URL effect to write ?savedView=<uuid>, violating the spec.
          const alreadyCommitted =
            activeViewRef.current?.kind === 'saved' &&
            activeViewRef.current.id === viewId;
          if (!alreadyCommitted) {
            setActiveView({ kind: 'saved', id: viewId });
          }
          setApplyToken(t => t + 1);
          setLastApplyDroppedCount(droppedCount);
          setLastApplyDroppedGroupCount(droppedGroupCount);
        } catch (err) {
          applyFailed = true;
          // The active view is committed only after the surface filters apply
          // cleanly above, so a throw here leaves the previous view in place —
          // there is nothing to revert.
          toast.error("This saved view couldn't be applied. Please try again.");
          logger.warn('Apply of saved view threw', {
            component: 'saved-views',
            saved_view_id: viewId,
            error_type: (err as Error)?.name ?? 'Unknown',
          });
        } finally {
          // Clear the applying flag INSIDE the transition so it commits
          // atomically with the filter application — clearing it urgently
          // (outside) could paint a frame with the flag down but the old
          // filters still applied, flashing stale data without a loading
          // state. Generation-guarded: a newer apply owns the flag.
          if (isCurrentGeneration()) {
            setIsApplyingView(false);
          }
        }
      });

      if (applyFailed) {
        return 'failed';
      }

      // The URL param is synced from the active view by useActiveListView; the
      // setActiveView commit above is what persists this view to the URL.

      // Build warning union: server warnings + client-side dropped-clause
      // warnings. Suppressed in preserveSurfaceFilters mode: the stored
      // filters were NOT applied, so warning about their dropped clauses
      // would mislead — the counts are still recorded above to keep the
      // explicit-save guard conservative.
      const allWarnings = preserveSurfaceFilters
        ? []
        : [...(view.warnings ?? []), ...droppedWarnings];
      if (allWarnings.length > 0) {
        const summary = allWarnings
          .slice(0, 3)
          .map(w => w.message)
          .join(' • ');
        const more =
          allWarnings.length > 3 ? ` (+${allWarnings.length - 3} more)` : '';
        toast.warning(
          'Some parts of this view were dropped: ' + summary + more,
          {
            duration: 8000,
          }
        );
      }

      return 'applied';
    },
    [currentOrg, queryClient, surface, setActiveView, startTransition]
  );

  // ---------------------------------------------------------------------------
  // selectAll — return to the ephemeral "All" view
  //
  // Resets filter conditions AND, when the surface tracks view state
  // (columns/sort/etc), restores that to EMPTY_VIEW_STATE so stale columns
  // from a previously-applied saved view don't persist into the "All" view.
  // Filter-only surfaces (no applyViewState) just get the filter reset.
  // ---------------------------------------------------------------------------
  // Reset ONLY the surface — filter conditions + view state — to the base "All"
  // state, leaving the active-view identity to the caller. Claims a new
  // generation BEFORE aborting so an in-flight applyView sees itself superseded:
  // its generation-guarded cleanup then no-ops (it no longer owns the flag),
  // exactly as a newer applyView would supersede it.
  // Invalidate any in-flight saved-view apply WITHOUT touching the surface
  // filters/columns. Claims a new generation BEFORE aborting so the pending
  // apply's generation-guarded commit no-ops (it no longer owns the flag),
  // exactly as a newer applyView would supersede it, then aborts the fetch. The
  // superseded apply's abort handler no longer clears the applying flag (its
  // generation guard now fails), so clear it here; idempotent when no apply is
  // in flight. This is the surface-preserving counterpart to resetSurface: used
  // when switching INTO a built-in view while a saved-view apply is still
  // pending — without it the late commit would setActiveView({ kind: 'saved' })
  // and clobber the built-in the user just selected.
  const cancelPendingApply = useCallback(() => {
    applyGenerationRef.current += 1;
    applyAbortRef.current?.abort();
    setIsApplyingView(false);
  }, []);

  // The surface-reset body itself. Extracted so it can be run INSIDE a caller's
  // transition — selectAll needs the identity reset to share one transition
  // with it (see below).
  const resetSurfaceState = useCallback(() => {
    surface.applyFilterConditionGroups([]);
    if (surface.applyViewState) {
      surface.applyViewState(EMPTY_VIEW_STATE as unknown as TViewState);
    }
    setApplyToken(t => t + 1);
    setLastApplyDroppedCount(0);
    setLastApplyDroppedGroupCount(0);
  }, [surface]);

  const resetSurface = useCallback(() => {
    cancelPendingApply();
    lastApplyRequestedViewIdRef.current = null;
    startTransition(resetSurfaceState);
  }, [cancelPendingApply, resetSurfaceState, startTransition]);

  // Select the "All"/default view: reset the surface AND the active view to the
  // default (the first built-in view, or null "All" when none are configured).
  // The URL param is synced from the active view by useActiveListView.
  //
  // Both resets MUST land in ONE transition. Splitting them — an urgent
  // setActiveView plus a transition-lane filter reset — makes React commit an
  // intermediate state of "no saved view active, but the old view's filters
  // still applied". useListUrlSync mirrors that commit faithfully and writes
  // `?filters=<the view we just left>`; its URL->state effect then reads that
  // param back (the surface has since reset, so the values differ) and applies
  // the old filters, dropping the user back into the view they clicked away
  // from. One transition means that intermediate state never commits.
  const selectAll = useCallback(() => {
    cancelPendingApply();
    lastApplyRequestedViewIdRef.current = null;
    startTransition(() => {
      resetSurfaceState();
      resetToDefaultView();
    });
  }, [
    cancelPendingApply,
    resetSurfaceState,
    resetToDefaultView,
    startTransition,
  ]);

  // ---------------------------------------------------------------------------
  // Save-modal lifecycle
  // ---------------------------------------------------------------------------
  const openSaveModal = useCallback(() => {
    const { filter_groups } = convertConditionGroupsToFilterGroups(
      surface.getFilterConditionGroups()
    );
    const viewState: SavedFilterViewState = surface.captureViewState
      ? (surface.captureViewState() as unknown as SavedFilterViewState)
      : EMPTY_VIEW_STATE;
    setSavePayload({
      filter_definition: { version: 1, filter_groups },
      view_state: viewState,
    });
    setSaveModalOpen(true);
  }, [surface]);

  const closeSaveModal = useCallback(() => {
    setSaveModalOpen(false);
  }, []);

  // Saving the surface as a new view makes that view the active one: the user
  // just named what is on screen, so the pill and the URL should say so rather
  // than leaving them on "All" with a matching-but-unattributed filter set.
  // Selecting it also clears `?filters=` — a saved view never renders its
  // filters in the URL, so the param the ad-hoc state was using goes away.
  const onViewSaved = useCallback(
    (view?: SavedFilterView) => {
      toast.success('Saved view created');
      if (!view) {
        return;
      }
      // The surface already holds exactly this view's filters and view_state —
      // it was built from them. Claim the apply so the reconciliation effect
      // does not re-fetch and re-apply what is already on screen.
      lastApplyRequestedViewIdRef.current = view.entity_id;
      setActiveView({ kind: 'saved', id: view.entity_id });
    },
    [setActiveView]
  );

  const onViewDeleted = useCallback(
    (viewId: string) => {
      if (activeViewId === viewId) {
        selectAll();
      }
    },
    [activeViewId, selectAll]
  );

  // ---------------------------------------------------------------------------
  // Saved-view dirty tracking + explicit save/discard.
  //
  // Saved views are NEVER auto-saved: edits to filters or columns/sort on an
  // active saved view are local until the user explicitly saves them (or
  // discards). This block computes whether the surface diverges from the
  // view's stored state and exposes the explicit save/discard actions. The
  // URL sync hook mirrors filter divergence into `?filters=`.
  // ---------------------------------------------------------------------------
  const activeSavedView = useMemo(
    () =>
      activeViewId != null
        ? (views?.find(v => v.entity_id === activeViewId) ?? null)
        : null,
    [views, activeViewId]
  );

  // The stored definition partitioned to this surface's applicable clauses —
  // comparing against the applicable partition (not the raw definition)
  // prevents false-dirty on surfaces that legitimately drop clauses at apply
  // time (e.g. pipeline scoping clauses). Null while unknown; a throwing
  // predicate degrades to unknown rather than crashing the page.
  const activeSavedViewFilterGroups = useMemo<FilterGroup[] | null>(() => {
    if (!activeSavedView) {
      return null;
    }
    try {
      return partitionApplicableFilterGroups(
        activeSavedView.filter_definition.filter_groups,
        surface.isClauseApplicable
      ).applicableGroups;
    } catch (err) {
      logger.warn(
        'surface.isClauseApplicable threw while computing saved view dirty baseline',
        {
          component: 'saved-views',
          saved_view_id: activeSavedView.entity_id,
          error_type: (err as Error)?.name ?? 'Unknown',
        }
      );
      return null;
    }
    // surface.isClauseApplicable is a stable useCallback ref per the
    // SavedViewSurface contract — intentional narrow dep.
  }, [activeSavedView, surface.isClauseApplicable]);

  // Extracted so the memos below can depend on the getter identities alone —
  // stable useCallback refs per the SavedViewSurface contract whose identity
  // changes exactly when the underlying state changes (the identity IS the
  // recompute signal). The functions are plain closures with no `this`
  // binding, so calling them detached from `surface` is safe.
  const surfaceGetFilterConditionGroups = surface.getFilterConditionGroups;
  const surfaceCaptureViewState = surface.captureViewState;

  const filtersDirtyState = useMemo<SavedViewDirtyState>(() => {
    if (activeViewId === null) {
      return 'clean';
    }
    if (isApplyingView || activeSavedViewFilterGroups === null) {
      return 'unknown';
    }
    const { filter_groups: currentGroups } =
      convertConditionGroupsToFilterGroups(surfaceGetFilterConditionGroups());
    return canonicalizeFilterGroups(currentGroups) ===
      canonicalizeFilterGroups(activeSavedViewFilterGroups)
      ? 'clean'
      : 'dirty';
  }, [
    activeViewId,
    isApplyingView,
    activeSavedViewFilterGroups,
    surfaceGetFilterConditionGroups,
  ]);

  // Columns/sort divergence from the stored view_state. False on surfaces
  // that don't capture view state (pipeline) — they have no columns to
  // diverge. Normalized comparison (see savedViewStatesEqual) so server key
  // order and equivalent sparse visibility maps never read as dirty.
  const viewStateDirty = useMemo<boolean>(() => {
    if (!activeSavedView || !surfaceCaptureViewState) {
      return false;
    }
    const captured =
      surfaceCaptureViewState() as unknown as SavedFilterViewState;
    return !savedViewStatesEqual(captured, activeSavedView.view_state);
  }, [activeSavedView, surfaceCaptureViewState]);

  const viewDirtyState = useMemo<SavedViewDirtyState>(() => {
    if (filtersDirtyState === 'unknown') {
      return 'unknown';
    }
    if (filtersDirtyState === 'dirty' || viewStateDirty) {
      return 'dirty';
    }
    return 'clean';
  }, [filtersDirtyState, viewStateDirty]);

  const canWriteActiveView =
    activeSavedView != null &&
    (activeSavedView.is_owner ||
      hasPermissionGroup(VIEW_ADMIN_PERMISSION_GROUP));

  const saveViewChanges = useCallback(() => {
    if (activeViewId === null) {
      return;
    }
    // Some of the view's stored clauses were dropped as inapplicable on this
    // surface — saving the surface's filters would silently strip them from
    // the view. Only reachable on surfaces with an isClauseApplicable
    // predicate (pipeline); guide toward "Save as new view" instead.
    if (lastApplyDroppedCount > 0 || lastApplyDroppedGroupCount > 0) {
      toast.warning(
        "Some of this view's filters aren't supported here, so saving would remove them. Save as a new view instead."
      );
      return;
    }
    const { filter_groups } = convertConditionGroupsToFilterGroups(
      surface.getFilterConditionGroups()
    );
    // Persist view_state only when this surface captures it — a filter-only
    // surface must not clobber the stored columns/sort (PATCH updates only
    // the properties present in the body).
    const viewState = surface.captureViewState
      ? (surface.captureViewState() as unknown as SavedFilterViewState)
      : undefined;
    // Captured locally (not just via the ref) so the .finally() below only
    // clears the ref if it still belongs to THIS call — guards against a
    // second overlapping save (for a different view) clobbering the marker.
    const viewIdBeingSaved = activeViewId;
    savingViewIdRef.current = viewIdBeingSaved;
    updateView({
      viewId: activeViewId,
      body: {
        filter_definition: { version: 1, filter_groups },
        ...(viewState !== undefined && { view_state: viewState }),
      },
    })
      .then(() => {
        toast.success('View updated');
      })
      .catch(() => {
        toast.error("Couldn't save changes to this view.");
        logger.warn('Explicit save of saved view changes failed', {
          component: 'saved-views',
          saved_view_id: activeViewId,
        });
      })
      .finally(() => {
        if (savingViewIdRef.current === viewIdBeingSaved) {
          savingViewIdRef.current = null;
        }
      });
  }, [
    activeViewId,
    lastApplyDroppedCount,
    lastApplyDroppedGroupCount,
    surface,
    updateView,
  ]);

  const discardViewChanges = useCallback(() => {
    if (activeSavedViewFilterGroups === null || !activeSavedView) {
      return;
    }
    startTransition(() => {
      // Re-apply the stored filters AND stored view_state (columns/sort) in
      // one transition. The applyToken bump signals a programmatic apply; the
      // dirty state flips to 'clean' and the URL sync hook drops `?filters=`.
      surface.applyFilterConditionGroups(
        filterGroupsToConditionGroups(activeSavedViewFilterGroups)
      );
      if (surface.applyViewState) {
        surface.applyViewState(
          activeSavedView.view_state as unknown as TViewState
        );
      }
      setApplyToken(t => t + 1);
    });
  }, [activeSavedViewFilterGroups, activeSavedView, surface, startTransition]);

  // ---------------------------------------------------------------------------
  // Abort in-flight apply on unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      applyAbortRef.current?.abort();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // URL persistence — hydrate from ?savedView=<id> once org context is ready
  // so that a list↔board surface toggle (which remounts the component) or a
  // deep-link to a saved-view URL re-applies the same saved view automatically.
  //
  // Gated on currentOrg because applyView no-ops without it; running once on
  // mount alone would silently drop the URL view when org context loads
  // asynchronously. The ref guard ensures we only fire once even though the
  // effect re-runs when applyView writes back to the URL.
  // ---------------------------------------------------------------------------
  const applyViewRef = useRef(applyView);
  applyViewRef.current = applyView;
  // Track the latest active view so the async retry/failure decision below reads
  // the current identity rather than the value closed over when the effect ran.
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;
  // Held in a ref so referencing it does not add it to the hydration effect's
  // deps and re-trigger the effect.
  const resetToDefaultViewRef = useRef(resetToDefaultView);
  resetToDefaultViewRef.current = resetToDefaultView;
  const hasHydratedFromUrlRef = useRef(false);
  const [hydrationRetry, setHydrationRetry] = useState(0);

  useEffect(() => {
    if (hasHydratedFromUrlRef.current || !currentOrg) {
      return;
    }
    // The active view is seeded synchronously from the URL by useActiveListView.
    // Only a saved-view identity needs an async apply here — a built-in / "All"
    // view is already reflected by its synchronous baseline. Mark hydration
    // attempted eagerly so nothing re-triggers a second apply on the success path.
    const hydrated = activeViewRef.current;
    hasHydratedFromUrlRef.current = true;
    if (hydrated?.kind !== 'saved') {
      return;
    }
    const viewId = hydrated.id;
    // When the URL also carries `?filters=` overrides (deep link to a dirty
    // view), the surface was seeded with them synchronously — apply only the
    // view's view_state + identity so the overrides survive. Idempotent
    // across the StrictMode abort/retry path: the ref read is live.
    const outcomePromise = applyViewRef.current(viewId, {
      preserveSurfaceFilters: hasUrlFilterOverrides(),
    });
    // Capture the generation AFTER applyView's synchronous prologue has run:
    // applyGenerationRef is incremented as the first statement in applyView
    // (before any await), so this read reflects the generation this call claimed.
    const capturedGeneration = applyGenerationRef.current;

    // True while THIS hydration is still the owner: a newer applyView (generation
    // moved on) or a user selection that changed the active view away from this
    // saved id means someone else now owns the identity and manages it.
    const stillHydratingThisView = () =>
      applyGenerationRef.current === capturedGeneration &&
      activeViewRef.current?.kind === 'saved' &&
      activeViewRef.current.id === viewId;

    outcomePromise
      .then(outcome => {
        if (outcome === 'aborted') {
          // A StrictMode/Suspense/remount cycle aborted the in-flight hydration
          // apply before it could apply the view. Reopen the guard and bump the
          // retry counter so this effect re-runs and re-applies. Genuine repeated
          // aborts fall back to the default view.
          //
          // Deliberately NOT gated on a "cancelled" flag set by this effect's
          // own cleanup: React's StrictMode double-invoke runs that cleanup
          // synchronously (before this .then() ever fires) even when nothing has
          // genuinely superseded this call, which would permanently suppress the
          // very retry this mechanism exists to enable. The generation/identity
          // check (stillHydratingThisView) is what actually detects a stale
          // retry, so it is sufficient on its own.
          const owns = stillHydratingThisView();
          const retryBudgetAvailable = hydrationRetry < MAX_HYDRATION_RETRIES;
          const willRetry = owns && retryBudgetAvailable;
          logger.info('Saved view hydration retry decision', {
            component: 'saved-views',
            saved_view_id: viewId,
            owns,
            retryCount: hydrationRetry,
            willRetry,
          });
          if (willRetry) {
            hasHydratedFromUrlRef.current = false;
            setHydrationRetry(n => n + 1);
            logger.info('Retrying saved view hydration after aborted apply', {
              component: 'saved-views',
              saved_view_id: viewId,
              retryCount: hydrationRetry + 1,
            });
          } else if (owns) {
            logger.warn('Saved view hydration retry limit exhausted', {
              component: 'saved-views',
              saved_view_id: viewId,
              retryCount: hydrationRetry,
            });
            // Unrecoverable abort: the deep-linked view could not be applied and
            // its baseline was suppressed by the seed. Restore the default view
            // so the query is not left silently unscoped.
            resetToDefaultViewRef.current();
          }
          // A non-matching generation/identity means a newer apply or user
          // selection superseded this hydration; that owner manages the active
          // view, so we deliberately do NOT reset here.
        } else if (outcome === 'failed') {
          // The deep-linked view could not be fetched/applied. Restore the
          // default view (its baseline was suppressed by the URL seed), unless a
          // newer apply/selection has since taken over the identity — guarded
          // exactly like the aborted path above.
          if (stillHydratingThisView()) {
            resetToDefaultViewRef.current();
          }
        }
      })
      .catch((err: unknown) => {
        logger.warn('Saved view hydration threw unexpectedly', {
          component: 'saved-views',
          org_id: currentOrg?.id,
          saved_view_id: viewId,
          error_type: (err as Error)?.name ?? 'Unknown',
        });
      });
  }, [currentOrg, hydrationRetry]);

  // ---------------------------------------------------------------------------
  // Back/forward reconciliation — when the active saved-view identity changes
  // WITHOUT an applyView call (browser back/forward: useActiveListView's
  // URL→state effect updates the pill but nothing re-applies the view's
  // filters), re-apply that view. Pill clicks and URL hydration are excluded:
  // their applyView call records the requested id synchronously (in its
  // prologue) before this effect observes the identity change. A restored
  // dirty URL (`?savedView=B&filters=…`) keeps its overrides: the URL sync
  // hook applies the decoded filters and this re-apply preserves them,
  // landing only view_state + identity. Races with a user pill-click are
  // handled by applyView's existing generation guards.
  //
  // Also handles the preference-driven case: when useActiveListView's one-shot
  // default-application effect sets activeView to a saved id (after both the
  // prefs + saved-views queries settle), lastApplyRequestedViewIdRef is still
  // null/stale, so this effect fires and applies the preference view.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (activeViewId === null) {
      // "All"/built-in became active (user selection or popstate): nothing to
      // re-apply here — clear the marker so navigating forward to a saved
      // view re-applies it.
      lastApplyRequestedViewIdRef.current = null;
      return;
    }
    if (!hasHydratedFromUrlRef.current) {
      // Initial mount on a saved-view URL: the hydration effect owns this.
      return;
    }
    if (activeViewId === lastApplyRequestedViewIdRef.current) {
      return;
    }
    void applyViewRef.current(activeViewId, {
      preserveSurfaceFilters: hasUrlFilterOverrides(),
    });
  }, [activeViewId]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    views,
    activeViewId,
    activeSavedView,
    applyToken,
    applyView,
    filtersDirtyState,
    viewDirtyState,
    activeSavedViewFilterGroups,
    canWriteActiveView,
    saveViewChanges,
    isSavingViewChanges,
    discardViewChanges,
    isApplyingView,
    surfaceTransitionPending,
    selectAll,
    resetSurface,
    cancelPendingApply,
    saveModalOpen,
    savePayload,
    openSaveModal,
    closeSaveModal,
    onViewSaved,
    onViewDeleted,
    lastApplyDroppedCount,
    lastApplyDroppedGroupCount,
  };
}
